import { motion } from 'framer-motion';
import { ChevronRight, RefreshCw } from 'lucide-react';

/** Shared shell so the snapshot slides read like the calories and cardio cards. */
export const SnapshotShell = ({ bg, children, testId }) => (
  <div
    className="relative h-full overflow-hidden rounded-3xl border border-white/[0.08] bg-gradient-to-b from-white/[0.04] to-white/[0.01]"
    data-testid={testId}
  >
    <img
      src={bg}
      alt=""
      className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.72]"
      loading="lazy"
      decoding="async"
    />
    <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#070806]/55 via-[#070806]/40 to-[#070806]/95" />
    <span
      className="pointer-events-none absolute inset-x-0 top-1/4 h-1/2"
      style={{ background: 'radial-gradient(ellipse at center, rgba(7,8,6,0.88) 0%, rgba(7,8,6,0.4) 45%, transparent 75%)' }}
    />
    <div className="relative z-[1] px-4 pb-4 pt-4">{children}</div>
  </div>
);

export const SnapshotHeader = ({ image, titleTop, titleBottom, subtitle, chip, action }) => (
  <>
    <div className="flex items-center gap-2.5">
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/[0.08] bg-black p-[3px]"
        style={{ boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.9), 0 6px 18px rgba(0,0,0,0.6)' }}
      >
        <img
          src={image}
          alt=""
          width={48}
          height={48}
          loading="lazy"
          decoding="async"
          className="h-full w-full rounded-[12px] object-contain"
          data-testid="snapshot-header-icon"
        />
      </span>
      <h3 className="min-w-0 flex-1 font-heading text-[21px] font-black uppercase leading-[0.95] tracking-tight sm:text-[26px]">
        <span className="block truncate text-white">{titleTop}</span>
        <span
          className="block truncate bg-clip-text text-transparent"
          style={{ backgroundImage: 'linear-gradient(90deg, rgba(var(--accent-rgb),0.55), var(--accent-hex))' }}
        >{titleBottom}</span>
      </h3>
      {chip}
      {action}
    </div>
    <p className="mt-2 text-[13px] font-medium leading-snug text-white/50">{subtitle}</p>
  </>
);

/** One readout: circular icon chip, big value, label underneath. */
export const SnapshotStat = ({ icon: Icon, value, label, sub, tint, chevron = true, testId }) => (
  <div
    className={`relative rounded-2xl border px-3 py-3 ${tint
      ? 'border-accent/30 bg-gradient-to-b from-accent/[0.16] to-accent/[0.03]'
      : 'border-white/[0.09] bg-white/[0.04]'}`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 overflow-hidden">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tint ? 'bg-accent/25 text-accent' : 'bg-white/[0.08] text-white/65'}`}>
        <Icon size={14} />
      </span>
      <span className={`line-clamp-2 min-w-0 flex-1 break-words font-heading font-black capitalize leading-[1.05] text-white ${
        String(value).length > 12 ? 'text-[13px]'
          : String(value).length > 9 ? 'text-[15px]'
            : String(value).length > 6 ? 'text-[17px]'
              : String(value).length > 4 ? 'text-[19px]' : 'text-[21px]'
      }`}>{value}</span>
      {chevron && <ChevronRight size={14} className="shrink-0 text-white/25" />}
    </div>
    <p className="mt-2 text-[11px] font-medium leading-tight text-white/55">
      {label}{sub && <><br />{sub}</>}
    </p>
  </div>
);

export const SnapshotChip = ({ children, tone = 'accent', testId }) => (
  <span
    className={`shrink-0 self-start rounded-full border px-2.5 py-1 font-heading text-[10px] font-black uppercase tracking-wider backdrop-blur-sm ${
      tone === 'accent' ? 'border-accent/40 bg-accent/15 text-accent' : 'border-white/15 bg-white/[0.06] text-white/60'
    }`}
    data-testid={testId}
  >
    {children}
  </span>
);

export const RegenerateButton = ({ onClick, busy }) => (
  <motion.button
    type="button"
    onClick={onClick}
    disabled={busy}
    whileTap={{ scale: 0.95 }}
    className="flex shrink-0 self-start items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.05] px-2.5 py-1 font-heading text-[10px] font-black uppercase tracking-wider text-white/70 disabled:opacity-50"
    data-testid="regenerate-btn"
  >
    <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> Regen
  </motion.button>
);
